import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { TeamMode } from '#src/searchCommonPlayer/teamMode.ts';
import {
  useDerivedTeamMode,
  useSyncTeamModeUrl,
} from '#src/searchCommonPlayer/teamMode.ts';

type TeamModeSupport = {
  derivedTeamMode: TeamMode;
  withAutoMode: boolean;
};

const TeamModeSupportContext = createContext<TeamModeSupport | null>(null);

// null when the surface doesn't support team-mode scoping (no provider)
export const useTeamModeSupport = () => useContext(TeamModeSupportContext);

// opt-in for surfaces whose queries consume `?tm=`
export const TeamModeSupportProvider = ({
  hasSo5CompetitionSlug = false,
  // AUTO only makes sense where a mode can be derived (scouting center tabs)
  withAutoMode = true,
  children,
}: {
  hasSo5CompetitionSlug?: boolean;
  withAutoMode?: boolean;
  children: ReactNode;
}) => {
  const derivedTeamMode = useDerivedTeamMode(hasSo5CompetitionSlug);
  useSyncTeamModeUrl({ hasSo5CompetitionSlug });

  const value = useMemo(
    () => ({ derivedTeamMode, withAutoMode }),
    [derivedTeamMode, withAutoMode]
  );

  return (
    <TeamModeSupportContext.Provider value={value}>
      {children}
    </TeamModeSupportContext.Provider>
  );
};
